import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api, { API_URL } from '../utils/api';
import axios from 'axios';

const AuthContext  = createContext();
const API_BASE     = API_URL;
const INACTIVE_MS  = 3 * 60 * 60 * 1000; // 3 hours inactivity = auto logout
const REFRESH_BEFORE_MS = 60 * 1000;      // refresh token 60s before expiry

const getTokenExpiry = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1])).exp * 1000;
  } catch { return null; }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]     = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshTimerRef    = useRef(null); // scheduled token refresh
  const inactiveTimerRef   = useRef(null); // scheduled inactivity logout
  const lastActivityRef    = useRef(Date.now());
  const isLoggedInRef      = useRef(false);

  // ── Force logout ──────────────────────────────────────────────────────────
  const doLogout = useCallback(() => {
    clearTimeout(refreshTimerRef.current);
    clearTimeout(inactiveTimerRef.current);
    isLoggedInRef.current = false;
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  // ── Reset inactivity timer — called on every user action ─────────────────
  const resetInactiveTimer = useCallback(() => {
    if (!isLoggedInRef.current) return;
    lastActivityRef.current = Date.now();
    clearTimeout(inactiveTimerRef.current);
    inactiveTimerRef.current = setTimeout(() => {
      // 3 hours passed with no activity — logout
      doLogout();
      window.location.href = '/login';
    }, INACTIVE_MS);
  }, [doLogout]);

  // ── Schedule token refresh ────────────────────────────────────────────────
  const scheduleRefresh = useCallback((token, refreshToken) => {
    clearTimeout(refreshTimerRef.current);
    if (!token || !refreshToken) return;
    const expiry = getTokenExpiry(token);
    if (!expiry) return;
    const msLeft    = expiry - Date.now();
    const refreshIn = Math.max(msLeft - REFRESH_BEFORE_MS, 0);

    refreshTimerRef.current = setTimeout(async () => {
      // Only refresh if user was active recently (within INACTIVE_MS)
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs >= INACTIVE_MS) {
        // User has been inactive for 3h — logout instead of refreshing
        doLogout();
        window.location.href = '/login';
        return;
      }
      try {
        const { data } = await api.post('/auth/refresh-token', { refreshToken });
        const newToken = data.token;
        localStorage.setItem('token', newToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        scheduleRefresh(newToken, refreshToken);
      } catch {
        doLogout();
        window.location.href = '/login';
      }
    }, refreshIn);
  }, [doLogout]);

  // ── Activity listeners ────────────────────────────────────────────────────
  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    const handler = () => resetInactiveTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, handler));
  }, [resetInactiveTimer]);

  // ── Save session helper ───────────────────────────────────────────────────
  const saveSession = useCallback((token, refreshToken, userData) => {
    localStorage.setItem('token', token);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    isLoggedInRef.current = true;
    setUser(userData);
    scheduleRefresh(token, refreshToken);
    resetInactiveTimer();
  }, [scheduleRefresh, resetInactiveTimer]);

  // ── On mount: restore session ─────────────────────────────────────────────
  useEffect(() => {
    const stored       = localStorage.getItem('user');
    const token        = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');

    if (stored && token) {
      const expiry = getTokenExpiry(token);
      if (expiry && Date.now() < expiry) {
        // Token still valid — restore
        isLoggedInRef.current = true;
        setUser(JSON.parse(stored));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        scheduleRefresh(token, refreshToken);
        resetInactiveTimer();
        setLoading(false);
      } else if (refreshToken) {
        // Token expired — try refresh
        api.post('/auth/refresh-token', { refreshToken })
          .then(({ data }) => {
            localStorage.setItem('token', data.token);
            api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
            isLoggedInRef.current = true;
            setUser(JSON.parse(stored));
            scheduleRefresh(data.token, refreshToken);
            resetInactiveTimer();
          })
          .catch(() => doLogout())
          .finally(() => setLoading(false));
        return;
      } else {
        doLogout();
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  // ── Unified Frontend Login ────────────────────────────────────────────────
  const login = async (email, password) => {
    // Clear any existing session first to prevent conflicts
    doLogout();

    try {
      // Perform a single POST request to the unified backend endpoint
      const { data } = await api.post('/auth/login', { email, password });
      saveSession(data.token, data.refreshToken, data.user);
      return data.user;
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Invalid credentials. Please verify your login ID and password.');
    }
  };

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = () => {
    const token = localStorage.getItem('token');
    if (token) api.post('/auth/logout').catch(() => {});
    doLogout();
  };

  // ── CP Login (for CP Panel UI authentication) ────────────────────────────
  const cpLogin = async (email, password, loginType) => {
    try {
      const { data } = await axios.post(`${API_BASE}/controlpanel/college-login`, { 
        loginId: email, 
        password, 
        loginType 
      });
      saveSession(data.token, data.refreshToken, data.user);
      return data.user;
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Invalid credentials');
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, cpLogin, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);